import PropTypes from 'prop-types'
import { MaskedInput } from 'baseui/input'
import { mergeOverrides } from 'baseui'
import { SMaskedInputOverrides } from './styled-components'
import { Alert, Delete } from 'baseui/icon'
import IconCarbonCheckmarkOutline from '@/shared/icons/IconCarbonCheckmarkOutline'
import InfoIcon from '../icon_info.svg'
import { useEffect, useState } from 'react'
import { EnhancerRow, RelativeContainer } from '@/shared/theme/styled-components'
import { useStyletron } from 'baseui'
import usePulseScope from '@/shared/components/PulseScope/usePulseScope'
import SDFieldLabel from '../SDFieldLabel/SDFieldLabel'
import { PulseFieldContainer } from '../SDFieldLabel/styled-components'
import { SDInputPulseOverrides } from '../SDInput/styled-components'

const StatusEnhancer = ({ icon }) => <div className='sdinput-end-enhancer'>{icon}</div>
StatusEnhancer.propTypes = { icon: PropTypes.node }

const StatusWithExtraEnhancer = ({ icon, extra }) => (
  <EnhancerRow>
    <div className='sdinput-end-enhancer'>{icon}</div>
    {extra}
  </EnhancerRow>
)
StatusWithExtraEnhancer.propTypes = { icon: PropTypes.node, extra: PropTypes.node }

const resolveEndEnhancer = (icon, enhancerProp) => {
  if (!icon) return enhancerProp ?? null
  if (!enhancerProp) return <StatusEnhancer icon={icon} />
  return <StatusWithExtraEnhancer icon={icon} extra={enhancerProp} />
}

const SDMaskedInput = ({ label, overrides, status, currentValue, onBlur, error, missing = false, ...rest }) => {
  const [value, setValue] = useState(currentValue || null)
  const isPulse = usePulseScope()
  const [, theme] = useStyletron()
  const iconSize = isPulse ? 16 : 24

  const iconMapping = {
    valid: <IconCarbonCheckmarkOutline size={iconSize} color={isPulse ? theme.colors.successPulse : 'green'} />,
    info: <img src={InfoIcon} alt='Info' style={{ width: iconSize, height: iconSize }} />,
    warning: <Alert size={iconSize} color='orange' />,
    error: <Delete size={iconSize} color={isPulse ? theme.colors.dangerPulse : 'red'} />,
  }

  const statusIcon = status && iconMapping[status] ? iconMapping[status] : null

  const endEnhancer = resolveEndEnhancer(statusIcon, rest.endEnhancer)

  const hasEndEnhancer = !!endEnhancer
  const baseOverrides = isPulse
    ? SDInputPulseOverrides(error || false, missing)
    : SMaskedInputOverrides(error || false, hasEndEnhancer, !!label)
  const newOverrides = mergeOverrides(baseOverrides, overrides)
  const Container = isPulse ? PulseFieldContainer : RelativeContainer

  useEffect(() => {
    if (currentValue === null || currentValue === undefined) {
      setValue('')
      return
    }
    setValue(currentValue)
  }, [currentValue])

  return (
    <Container>
      {label && <SDFieldLabel label={label} />}
      <MaskedInput
        {...rest}
        error={error}
        overrides={newOverrides}
        onBlur={e => {
          if (typeof onBlur === 'function') {
            let selectedValue = e.target.value
            if (selectedValue === '') {
              selectedValue = null
            }
            onBlur(selectedValue)
          }
        }}
        onChange={e => setValue(e.target.value)}
        endEnhancer={endEnhancer}
        value={value}
      />
    </Container>
  )
}

SDMaskedInput.propTypes = {
  label: PropTypes.node,
  overrides: PropTypes.object,
  status: PropTypes.oneOf(['valid', 'info', 'warning', 'error']),
  currentValue: PropTypes.any,
  onBlur: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  missing: PropTypes.bool,
}

export default SDMaskedInput
